/**
 * Generador de archivo .ics para agregar la cita al calendario personal del paciente.
 * Formato estándar RFC 5545 — compatible con Google Calendar, Apple Calendar y Outlook
 * sin depender de ninguna API externa ni OAuth.
 */

import { toSantiagoISO } from './dateUtils';

export interface ICSEventInput {
  uid: string;
  title: string;
  description?: string;
  location?: string;
  date: string; // YYYY-MM-DD (America/Santiago)
  time: string; // HH:MM (America/Santiago)
  durationMinutes: number;
}

// Escapa texto según RFC 5545 (orden importa: barra invertida primero)
function escapeICSText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

function toICSDateUTC(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

/**
 * Resuelve inicio y fin del evento como instancias `Date` en UTC, partiendo de
 * la fecha/hora en America/Santiago. Compartido por el `.ics` y por los links
 * de calendario web (Google / Outlook) para que el offset DST se calcule una
 * sola vez y de la misma forma.
 */
export function getEventBounds(event: ICSEventInput): { start: Date; end: Date } {
  const start = new Date(toSantiagoISO(event.date, event.time));
  const end = new Date(start.getTime() + event.durationMinutes * 60 * 1000);
  return { start, end };
}

export function buildICSContent(event: ICSEventInput): string {
  const { start, end } = getEventBounds(event);
  const now = new Date();

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Conectemos Chile//Booking//ES',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.uid}@conectemoschile.cl`,
    `DTSTAMP:${toICSDateUTC(now)}`,
    `DTSTART:${toICSDateUTC(start)}`,
    `DTEND:${toICSDateUTC(end)}`,
    `SUMMARY:${escapeICSText(event.title)}`,
  ];

  if (event.description) {
    lines.push(`DESCRIPTION:${escapeICSText(event.description)}`);
  }
  if (event.location) {
    lines.push(`LOCATION:${escapeICSText(event.location)}`);
  }

  lines.push('STATUS:CONFIRMED', 'END:VEVENT', 'END:VCALENDAR');

  return lines.join('\r\n');
}

export function downloadICSEvent(event: ICSEventInput): void {
  const content = buildICSContent(event);

  // iOS Safari (y navegadores in-app tipo WKWebView) no soportan `blob:` + `<a download>`
  // para .ics: en vez de ofrecer "Agregar a calendario" descargan el archivo crudo a Archivos.
  // Navegar a un data: URI sí dispara el sheet nativo de Calendario en iOS.
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  if (isIOS) {
    window.location.href = `data:text/calendar;charset=utf-8,${encodeURIComponent(content)}`;
    return;
  }

  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.uid}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
